import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import type { GridColDef, GridRenderCellParams, GridRowParams } from "@mui/x-data-grid";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { useNavigate } from "react-router";
import { CreatedByColumn } from "../components/created-by-column.tsx";
import { DataGrid } from "../components/data-grid";
import { PageHeader } from "../components/page-header";
import TestSuiteDialog from "../components/test-suites/test-suite-dialog.tsx";
import { useApiMutation } from "../hooks/use-api-mutation.ts";
import { useApiQuery } from "../hooks/use-api-query.ts";
import type { RunSuiteByIdParams, Test, TestSuite, TestSuiteSchema } from "../types.ts";
import { PfsScoreChip } from "../utils/pfs-utils.tsx";

interface SuiteActionColumnProps extends GridRenderCellParams<TestSuite> {
  onDelete: () => void;
}

const SuiteActionColumn = ({ row, onDelete }: SuiteActionColumnProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const nav = useNavigate();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const { mutate: runSuite, isPending: isRunningSuite } = useApiMutation<void, RunSuiteByIdParams>(
    "POST",
    `/test-suites/${row.id}/run`,
    {
      onSuccess: (resp) => {
        enqueueSnackbar(
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <Typography variant="body1">Test suite run started successfully!</Typography>
            <IconButton
              aria-label="View suite run"
              onClick={() => {
                nav(`/suite-runs/${resp.run_id}`);
              }}
              size="small"
              color="inherit"
            >
              <OpenInNewIcon htmlColor="white" />
            </IconButton>
          </Stack>,
          {
            variant: "success",
            anchorOrigin: {
              horizontal: "right",
              vertical: "bottom",
            },
          },
        );
      },
    },
  );

  const { mutate: deleteSuite, isPending: isDeletingSuite } = useApiMutation<void, void>(
    "DELETE",
    `/test-suites/${row.id}`,
    {
      onSuccess: () => {
        enqueueSnackbar("Test suite deleted successfully", {
          variant: "success",
          anchorOrigin: {
            horizontal: "right",
            vertical: "bottom",
          },
        });

        // Call the external handler to refresh the suites list
        onDelete();
        setDeleteConfirmOpen(false);
      },
      onError: (error) => {
        enqueueSnackbar(`Error deleting test suite: ${error.message}`, {
          variant: "error",
          anchorOrigin: {
            horizontal: "right",
            vertical: "bottom",
          },
        });
        setDeleteConfirmOpen(false);
      },
    },
  );

  const handleDeleteConfirm = () => {
    deleteSuite({});
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
  };

  return (
    <>
      <Box alignItems="center" display="flex">
        <Tooltip title="Run suite">
          <IconButton
            size="small"
            color="default"
            disabled={isRunningSuite}
            onClick={() => {
              runSuite({});
            }}
            aria-label="Run suite"
          >
            <PlayCircleIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Delete test suite">
          <IconButton
            size="small"
            color="error"
            disabled={isDeletingSuite}
            onClick={() => setDeleteConfirmOpen(true)}
            aria-label="Delete test suite"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Confirm Test Suite Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete the test suite "{row.name}"? This action will soft
            delete the test suite and it will no longer be visible.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus disabled={isDeletingSuite}>
            {isDeletingSuite ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default function TestSuitesPage() {
  const [newSuite, setNewSuite] = useState<TestSuiteSchema | null>(null);
  const nav = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");

  const { enqueueSnackbar } = useSnackbar();

  const {
    data: suites,
    isLoading: isLoadingSuites,
    refetch: refetchSuites,
  } = useApiQuery<TestSuite[]>(
    searchQuery ? `/test-suites?q=${encodeURIComponent(searchQuery)}` : "/test-suites",
  );

  const { mutate: createSuite, isPending: isCreatingSuite } = useApiMutation<
    TestSuiteSchema,
    TestSuite
  >("POST", "/test-suites", {
    onSuccess: () => {
      enqueueSnackbar("Test suite created!", {
        variant: "success",
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });

      refetchSuites();
      setNewSuite(null);
    },
  });

  // Handle the delete event
  const handleDeleteSuite = () => {
    refetchSuites();
  };

  const handleRowClick = (params: GridRowParams<TestSuite>) => {
    nav(`/tests-main/test-suites/${params.row.id}`);
  };

  // Define columns with the action column renderer
  const testSuiteColumns: GridColDef<TestSuite>[] = [
    {
      field: "name",
      headerName: "Name",
      type: "string",
      minWidth: 240,
      flex: 1,
    },
    {
      field: "created_by",
      type: "string",
      headerName: "Created By",
      width: 150,
      renderCell: CreatedByColumn,
    },
    {
      field: "schedule",
      headerName: "Schedule",
      type: "string",
      width: 160,
      valueGetter: (val: TestSuite["schedule"]) => {
        return !val || val === "" ? "Manual" : val;
      },
    },
    {
      field: "schedule_enabled",
      headerName: "Enabled",
      type: "boolean",
      minWidth: 100,
    },
    {
      field: "test_ids",
      headerName: "Tests",
      type: "number",
      valueGetter: (val: TestSuite["test_ids"]) => {
        return val.length;
      },
      minWidth: 100,
    },
    {
      field: "pfs_score",
      headerName: "Suite PFS",
      width: 120,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        if (params.value == null) return "-";
        return <PfsScoreChip score={params.value} />;
      },
      description: "Suite flakiness based on historical suite runs (Probabilistic Flakiness Score)",
    },
    {
      field: "tests_pfs_score",
      headerName: "Tests PFS",
      width: 120,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        if (params.value == null) return "-";
        return <PfsScoreChip score={params.value} />;
      },
      description: "Average flakiness of tests in this suite",
    },
    {
      field: "created_at",
      type: "dateTime",
      headerName: "Created At",
      valueGetter: (val: Test["created_at"]) => {
        return new Date(val);
      },
      minWidth: 200,
      flex: 1,
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 200,
      renderCell: (params) => <SuiteActionColumn {...params} onDelete={handleDeleteSuite} />,
    },
  ];

  return (
    <Box data-testid="tests-page">
      <PageHeader
        title="Test Suites"
        subtitle="Manage your test suites"
        sx={{ borderRadius: 3 }}
        actions={
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              size="small"
              placeholder="Search by name, description, or ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: "grey.500" }} />,
              }}
              sx={{
                minWidth: 300,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  backgroundColor: "common.white",
                },
              }}
            />
            <Tooltip title="New Test Suite" placement="top">
              <IconButton
                size="small"
                onClick={() =>
                  setNewSuite({
                    name: "",
                    test_ids: [],
                    webhook_ids: [],
                    tag_filters: [],
                    schedule_enabled: false,
                  })
                }
                sx={{
                  borderRadius: 2,
                  p: 1.5,
                  border: "0px solid",
                  borderColor: "divider",
                  color: "grey.800",
                  backgroundColor: "transparent",
                  "&:hover": {
                    borderColor: "info.dark",
                    backgroundColor: "common.white",
                  },
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        }
      />

      {!!newSuite && (
        <TestSuiteDialog
          open={!!newSuite}
          mode="create"
          onClose={() => setNewSuite(null)}
          onSave={(data) => createSuite(data)}
          data={newSuite}
          setData={(data) => setNewSuite(data as TestSuiteSchema)}
          isProcessing={isCreatingSuite}
        />
      )}

      <DataGrid
        loading={isLoadingSuites}
        rows={suites ?? []}
        columns={testSuiteColumns}
        pageSizeOptions={[25]}
        minHeight={`calc(100vh - 200px)`}
        onRowClick={handleRowClick}
      />
    </Box>
  );
}
